import {
  Analysis,
  Granularity,
  Metric,
} from '../../../dashboard-config/dashboard-config.model';
import { KpiMeasure } from '../../../dashboard-config/kpis/kpi-columns';
import { DashboardShellAxes } from '../../_shared/dashboard-axes';
import { TimeCardinality } from '../../_shared/time-filters';
import {
  DossierHeadingLabelUsage,
} from './dossier-metric.profile';
import { DossierStatesDefinition } from './dossier-states.definition';

export const DOSSIER_OVERVIEW_SHELL_ID = 'dossier-overview' as const;
export const DOSSIER_INFO_SHELL_ID = 'dossier-info' as const;
export const DOSSIER_DEVELOPMENT_SHELL_ID = 'dossier-development' as const;
export const DOSSIER_MARKET_SHELL_ID = 'dossier-market' as const;
export const DOSSIER_ONION_SHELL_ID = 'dossier-onion' as const;

export type DossierShellId =
  | typeof DOSSIER_OVERVIEW_SHELL_ID
  | typeof DOSSIER_INFO_SHELL_ID
  | typeof DOSSIER_DEVELOPMENT_SHELL_ID
  | typeof DOSSIER_MARKET_SHELL_ID
  | typeof DOSSIER_ONION_SHELL_ID;

export type DossierShell = 'overview' | 'info' | 'development' | 'market' | 'onion';

export type DossierHeadingFn = DossierStatesDefinition<unknown>['heading'];

/** A selected-KPI measure copied into a dossier row key. */
export interface DossierValueColumn {
  measure: KpiMeasure;
  resultColumn: string;
  /** Read the same type under another metric (count dossiers show revenue and vice versa). */
  fromMetric?: Metric;
}

/** Sankey value, parent value and share of the parent (overview, onion). */
const SANKEY_VALUE_COLUMNS: DossierValueColumn[] = [
  { measure: 'sankeyTargetValue', resultColumn: 'value' },
  { measure: 'sankeySourceValue', resultColumn: 'value_parent' },
  { measure: 'sankeyTargetShareOfSourcePct', resultColumn: 'value_parent_pct' },
];

/**
 * A dossier variant: its route suffix, dataset and filters. Every variant is Dossier × States;
 * the variant picks the view, as the shell does in the other families (D6).
 */
export interface DossierShellProfile extends DashboardShellAxes {
  id: DossierShellId;
  variant: string;
  datasetAnalysisToken: 'dssr' | 'dssr_sky';
  time: TimeCardinality;
  subMarket: 'none' | 'single' | 'multi';
  /** WZ class and layer filters, entity ids and sankey node names (Marktquerschnitt). */
  onion?: boolean;
  /** Columns every metric reads; info's columns differ per metric (`metric.valueColumns`). */
  valueColumns?: DossierValueColumn[];
  /** `rank` over `value` (Branchenstruktur). */
  rankByValue?: boolean;
  /** Info renders its own type labels, so rows get no `kpi_name`. */
  noKpiLabel?: boolean;
  /** Which metric-owned heading label this shell uses. */
  headingLabelUsage: DossierHeadingLabelUsage;
  createHeading: (subject: string) => DossierHeadingFn;
}

export const dossierShellProfiles: Record<DossierShell, DossierShellProfile> = {
  overview: {
    id: DOSSIER_OVERVIEW_SHELL_ID,
    analysisId: Analysis.Dossier,
    granularityId: Granularity.States,
    variant: 'marktstruktur',
    datasetAnalysisToken: 'dssr_sky',
    time: 'single',
    subMarket: 'single',
    valueColumns: SANKEY_VALUE_COLUMNS,
    headingLabelUsage: 'structure',
    createHeading: (subject: string) => (filterValues, product) =>
      `WIRTSCHAFTSSTANDORT ${filterValues.get('state')}: ${subject} DER ${product!.name}, ${filterValues.get('year')}`,
  },
  info: {
    id: DOSSIER_INFO_SHELL_ID,
    analysisId: Analysis.Dossier,
    granularityId: Granularity.States,
    variant: 'übersicht',
    datasetAnalysisToken: 'dssr',
    time: 'single',
    subMarket: 'none',
    noKpiLabel: true,
    headingLabelUsage: 'standalone',
    createHeading: (subject: string) => (filterValues) =>
      `WIRTSCHAFTSSTANDORT ${filterValues.get('state')}: ÜBERSICHT ${subject}, ${filterValues.get('year')}`,
  },
  development: {
    id: DOSSIER_DEVELOPMENT_SHELL_ID,
    analysisId: Analysis.Dossier,
    granularityId: Granularity.States,
    variant: 'entwicklung',
    datasetAnalysisToken: 'dssr',
    time: 'range',
    subMarket: 'none',
    valueColumns: [
      { measure: 'value', resultColumn: 'value' },
      { measure: 'changePrevYearPct', resultColumn: 'value_change_pct' },
    ],
    headingLabelUsage: 'genitive',
    createHeading: (subject: string) => (filterValues, product) =>
      `WIRTSCHAFTSSTANDORT ${filterValues.get('state')}: ENTWICKLUNG ${subject} DER ${product!.name}, ${filterValues.get('years').replace('-', 'BIS')}`,
  },
  market: {
    id: DOSSIER_MARKET_SHELL_ID,
    analysisId: Analysis.Dossier,
    granularityId: Granularity.States,
    variant: 'branchenstruktur',
    datasetAnalysisToken: 'dssr',
    time: 'single',
    subMarket: 'multi',
    valueColumns: [
      { measure: 'value', resultColumn: 'value' },
      { measure: 'shareOfSumSubMarketPct', resultColumn: 'value_pct' },
    ],
    rankByValue: true,
    headingLabelUsage: 'genitive',
    createHeading: (subject: string) => (filterValues, product) =>
      `WIRTSCHAFTSSTANDORT ${filterValues.get('state')}: VERTEILUNG ${subject} DER ${product!.name} NACH TEILMÄRKTEN, ${filterValues.get('year')}`,
  },
  onion: {
    id: DOSSIER_ONION_SHELL_ID,
    analysisId: Analysis.Dossier,
    granularityId: Granularity.States,
    variant: 'marktquerschnitt',
    datasetAnalysisToken: 'dssr_sky',
    time: 'single',
    subMarket: 'single',
    onion: true,
    valueColumns: SANKEY_VALUE_COLUMNS,
    headingLabelUsage: 'structure',
    createHeading: (subject: string) => (filterValues, product) =>
      `WIRTSCHAFTSSTANDORT ${filterValues.get('state')}: QUERSCHNITT DER ${subject} DER ${product!.name}, ${filterValues.get('year')}`,
  },
};
